import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Toaster, toast } from "sonner";
import {
  Lightning,
  Brain,
  Users,
  Globe,
  ChartLineUp,
  Shield,
  CheckCircle,
  ArrowRight,
  Envelope,
  MapPin,
  LinkedinLogo,
  TwitterLogo,
  List,
  X,
  Briefcase,
  Target,
  Eye,
  Heart,
  Rocket,
  GraduationCap,
  Buildings,
  Heartbeat,
  Bank,
  Factory,
  ShoppingCart,
  Code,
  Handshake
} from "@phosphor-icons/react";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Button } from "./components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Approach", href: "#approach" },
    { name: "Leadership", href: "#leadership" },
    { name: "Insights", href: "#insights" },
    { name: "Careers", href: "#careers" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-glassmorphism shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-2" data-testid="logo">
            <span className="font-heading text-2xl font-semibold text-[#0A1128]">
              GentryTechnologies
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-medium text-[#4A5568] hover:text-[#B87333] transition-colors duration-300"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              className="btn-primary text-sm"
              data-testid="nav-cta"
            >
              Engage With Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X size={24} className="text-[#0A1128]" />
            ) : (
              <List size={24} className="text-[#0A1128]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden bg-white border-t border-[#E2E8F0] py-4"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 text-[#4A5568] hover:text-[#B87333] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              className="btn-primary inline-block mt-4 text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Engage With Us
            </a>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      className="min-h-screen flex items-center pt-20 bg-[#FFFFFF]"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <AnimatedSection>
            <motion.span
              variants={fadeInUp}
              className="overline block mb-6"
            >
              Strategic Consulting
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-heading font-semibold text-[#0A1128] leading-tight mb-8"
            >
              Engineering Transformation.{" "}
              <span className="text-[#B87333]">Powering Intelligent Enterprises.</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#4A5568] mb-10 max-w-xl"
            >
              GentryTechnologies Consulting Group partners with forward-thinking organizations
              to deliver AI-driven innovation, enterprise transformation, and high-impact
              talent solutions that accelerate growth and operational excellence.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="hero-cta-primary">
                Engage With Us
              </a>
              <a href="#services" className="btn-secondary" data-testid="hero-cta-secondary">
                Explore Our Capabilities
              </a>
            </motion.div>
          </AnimatedSection>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="aspect-[4/3] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Executive team meeting"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-[#F8F7F4] p-6 rounded-sm shadow-lg">
              <p className="step-number mb-2">SINCE 1993</p>
              <p className="text-2xl font-heading font-semibold text-[#0A1128]">
                Trusted Partner
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity First",
      description: "We operate with transparency and accountability"
    },
    {
      icon: Target,
      title: "Client-Centricity",
      description: "Your success defines our success"
    },
    {
      icon: Rocket,
      title: "Innovation Mindset",
      description: "We challenge convention and embrace change"
    },
    {
      icon: CheckCircle,
      title: "Execution Excellence",
      description: "We deliver results, not just recommendations"
    }
  ];

  return (
    <section
      id="about"
      className="py-24 lg:py-32 bg-[#F8F7F4]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Who We Are */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                About Us
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-8"
              >
                Who We Are
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#4A5568] mb-6">
                GentryTechnologies Consulting Group is a strategic consulting and technology
                solutions firm focused on helping enterprises navigate complex
                transformations across digital, operational, and workforce ecosystems.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-[#4A5568]">
                We combine deep industry expertise with advanced technology insight
                to deliver solutions that are scalable, resilient, and future-ready.
              </motion.p>

              {/* Mission & Vision */}
              <motion.div variants={fadeInUp} className="mt-12 space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Eye size={32} weight="duotone" className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-[#0A1128] mb-2">
                      Our Mission
                    </h3>
                    <p className="text-[#4A5568]">
                      To enable organizations to achieve measurable business outcomes
                      through intelligent strategy, transformative technology, and
                      world-class execution.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Heart size={32} weight="duotone" className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-[#0A1128] mb-2">
                      Our Vision
                    </h3>
                    <p className="text-[#4A5568]">
                      To be a globally trusted consulting partner driving enterprise
                      reinvention through AI, digital innovation, and human capital
                      excellence.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right - Core Values */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Our Foundation
              </motion.span>
              <motion.h3
                variants={fadeInUp}
                className="text-3xl font-heading font-semibold text-[#0A1128] mb-8"
              >
                Core Values
              </motion.h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    variants={fadeInUp}
                    className="value-card"
                    data-testid={`value-card-${index}`}
                  >
                    <value.icon
                      size={40}
                      weight="duotone"
                      className="text-[#B87333] mb-4"
                    />
                    <h4 className="text-lg font-heading font-semibold text-[#0A1128] mb-2">
                      {value.title}
                    </h4>
                    <p className="text-sm text-[#4A5568]">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Brain,
      title: "Enterprise Transformation & Digital Strategy",
      description:
        "We help organizations reimagine their future by aligning business strategy with technology execution.",
      capabilities: [
        "Digital Transformation Roadmaps",
        "AI & Intelligent Automation Strategy",
        "Business Process Optimization",
        "Change Management & Adoption",
        "Enterprise Architecture Advisory"
      ]
    },
    {
      icon: Code,
      title: "Technology Consulting & Implementation",
      description:
        "We design, build, and optimize modern technology ecosystems.",
      capabilities: [
        "Cloud & Infrastructure Transformation",
        "Data, Analytics & AI Platforms",
        "Cybersecurity & Risk Management",
        "Enterprise Systems (ERP, CRM, Supply Chain)",
        "Integration & API Strategy"
      ]
    },
    {
      icon: Users,
      title: "Strategic Talent & Workforce Solutions",
      description:
        "We provide elite, pre-vetted professionals to accelerate delivery and innovation.",
      capabilities: [
        "AI / Machine Learning Engineers",
        "Cloud & DevOps Specialists",
        "Cybersecurity Experts",
        "ERP & Enterprise Architects",
        "Program & Product Leaders"
      ]
    },
    {
      icon: Globe,
      title: "Global Delivery & Workforce Scaling",
      description:
        "We enable flexible, cost-effective delivery models across global markets.",
      capabilities: [
        "Onshore Expertise",
        "Nearshore Delivery Centers",
        "Offshore Engineering Hubs"
      ]
    },
    {
      icon: ChartLineUp,
      title: "Managed Services & Program Delivery",
      description:
        "We take ownership of execution, ensuring outcomes—not just activity.",
      capabilities: [
        "End-to-End Program Management",
        "Agile & Scaled Delivery Models",
        "PMO & Governance Structures",
        "Performance Tracking & Reporting"
      ]
    }
  ];

  return (
    <section
      id="services"
      className="py-24 lg:py-32 bg-[#FFFFFF]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <motion.span variants={fadeInUp} className="overline block mb-4">
              What We Do
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
            >
              Our Services
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#4A5568] max-w-2xl mx-auto"
            >
              Comprehensive solutions designed to drive transformation across
              your enterprise.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeInUp}
                className={`service-card p-8 rounded-sm ${
                  index === 0 ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
                data-testid={`service-card-${index}`}
              >
                <service.icon
                  size={48}
                  weight="duotone"
                  className="text-[#B87333] mb-6"
                />
                <h3 className="text-2xl font-heading font-semibold text-[#0A1128] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#4A5568] mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.capabilities.map((cap) => (
                    <li
                      key={cap}
                      className="flex items-center gap-2 text-sm text-[#4A5568]"
                    >
                      <CheckCircle
                        size={16}
                        weight="fill"
                        className="text-[#B87333] flex-shrink-0"
                      />
                      {cap}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Approach Section
const ApproachSection = () => {
  const steps = [
    {
      number: "01",
      title: "Discover",
      description:
        "Align on business goals and transformation vision. We work closely with your leadership to understand objectives, challenges, and aspirations."
    },
    {
      number: "02",
      title: "Diagnose",
      description:
        "Assess current capabilities, gaps, and risks. Our experts conduct thorough analysis to identify opportunities and potential roadblocks."
    },
    {
      number: "03",
      title: "Design",
      description:
        "Architect tailored solutions and delivery strategies. We create comprehensive blueprints aligned with your unique requirements."
    },
    {
      number: "04",
      title: "Deliver",
      description:
        "Execute with precision using best-in-class talent. Our teams implement solutions with agility and attention to detail."
    },
    {
      number: "05",
      title: "Optimize",
      description:
        "Continuously improve performance and outcomes. We establish feedback loops and metrics to ensure lasting success."
    }
  ];

  return (
    <section
      id="approach"
      className="py-24 lg:py-32 bg-[#F8F7F4]"
      data-testid="approach-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left - Sticky Title */}
          <div className="lg:col-span-2 lg:sticky lg:top-32 lg:self-start">
            <AnimatedSection>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                How We Work
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
              >
                Our Approach
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#4A5568]">
                A structured model for consistent results. Our proven methodology
                ensures every engagement delivers measurable value.
              </motion.p>
            </AnimatedSection>
          </div>

          {/* Right - Steps */}
          <div className="lg:col-span-3 space-y-6">
            <AnimatedSection>
              {steps.map((step) => (
                <motion.div
                  key={step.number}
                  variants={fadeInUp}
                  className="approach-card p-8 rounded-sm"
                  data-testid={`approach-step-${step.number}`}
                >
                  <span className="step-number block mb-3">{step.number}</span>
                  <h3 className="text-2xl font-heading font-semibold text-[#0A1128] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#4A5568]">{step.description}</p>
                </motion.div>
              ))}
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const industries = [
    { name: "Healthcare & Life Sciences", icon: Heartbeat },
    { name: "Financial Services & Insurance", icon: Bank },
    { name: "Manufacturing & Supply Chain", icon: Factory },
    { name: "Retail, CPG & E-Commerce", icon: ShoppingCart },
    { name: "Technology & SaaS", icon: Code },
    { name: "Public Sector & Non-Profit", icon: Buildings }
  ];

  return (
    <section
      id="industries"
      className="py-24 lg:py-32 bg-[#FFFFFF]"
      data-testid="industries-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <motion.span variants={fadeInUp} className="overline block mb-4">
              Expertise
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
            >
              Industries We Serve
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#4A5568] max-w-2xl mx-auto"
            >
              We bring cross-industry expertise to solve complex challenges.
            </motion.p>
          </div>

          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-4"
          >
            {industries.map((industry, index) => (
              <div
                key={industry.name}
                className="industry-tag rounded-sm cursor-pointer"
                data-testid={`industry-${index}`}
              >
                <industry.icon size={20} weight="duotone" className="mr-2" />
                {industry.name}
              </div>
            ))}
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Why GentryTech Section
const WhyGentryTechSection = () => {
  const reasons = [
    {
      icon: Briefcase,
      title: "Executive-Level Expertise",
      description: "Led by transformation leaders with enterprise experience"
    },
    {
      icon: Lightning,
      title: "Speed-to-Value Delivery",
      description: "Accelerated timelines without compromising quality"
    },
    {
      icon: Brain,
      title: "AI-Driven Approach",
      description: "Leveraging intelligent tools to enhance outcomes"
    },
    {
      icon: Handshake,
      title: "Flexible Engagement Models",
      description: "Built around your business needs"
    },
    {
      icon: Globe,
      title: "Global Talent Network",
      description: "Access to top-tier professionals worldwide"
    }
  ];

  return (
    <section
      id="why-gentrytech"
      className="py-24 lg:py-32 bg-[#0A1128]"
      data-testid="why-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <motion.span
              variants={fadeInUp}
              className="overline block mb-4 text-[#B87333]"
            >
              Our Difference
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-4xl lg:text-5xl font-heading font-semibold text-white mb-6"
            >
              Why GentryTechnologies
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-gray-300 max-w-2xl mx-auto"
            >
              Where Strategy Meets Execution
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                variants={fadeInUp}
                className="text-center lg:text-left"
                data-testid={`why-card-${index}`}
              >
                <reason.icon
                  size={48}
                  weight="duotone"
                  className="text-[#B87333] mb-4 mx-auto lg:mx-0"
                />
                <h3 className="text-xl font-heading font-semibold text-white mb-2">
                  {reason.title}
                </h3>
                <p className="text-gray-400">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Client Impact Section
const ClientImpactSection = () => {
  const impacts = [
    "Reduced operational costs through automation and process redesign",
    "Accelerated digital transformation timelines by 30–50%",
    "Improved system adoption and workforce productivity",
    "Delivered scalable, future-ready technology platforms"
  ];

  const testimonials = [
    {
      quote:
        "GentryTechnologies didn't just provide talent—they delivered transformation.",
      author: "Fortune 500 CTO"
    },
    {
      quote: "Their ability to connect strategy with execution is unmatched.",
      author: "Healthcare CEO"
    },
    {
      quote: "A true partner in navigating complex enterprise change.",
      author: "Financial Services VP"
    }
  ];

  return (
    <section
      id="impact"
      className="py-24 lg:py-32 bg-[#F8F7F4]"
      data-testid="impact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Impact */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Results
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-8"
              >
                Client Impact
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-[#4A5568] mb-8"
              >
                Driving Measurable Results
              </motion.p>

              <div>
                {impacts.map((impact, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="impact-item"
                    data-testid={`impact-item-${index}`}
                  >
                    <CheckCircle
                      size={24}
                      weight="fill"
                      className="text-[#B87333] flex-shrink-0 mt-1"
                    />
                    <p className="text-[#4A5568]">{impact}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right - Testimonials */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Testimonials
              </motion.span>
              <motion.h3
                variants={fadeInUp}
                className="text-3xl font-heading font-semibold text-[#0A1128] mb-8"
              >
                What Our Clients Say
              </motion.h3>

              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="testimonial-card p-8 rounded-sm"
                    data-testid={`testimonial-${index}`}
                  >
                    <p className="text-lg text-[#0A1128] mb-4 italic">
                      "{testimonial.quote}"
                    </p>
                    <p className="text-sm text-[#B87333] font-medium">
                      — {testimonial.author}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Insights Section
const InsightsSection = () => {
  const insights = [
    {
      title: "The Future of AI in Enterprise Operations",
      category: "AI & Automation"
    },
    {
      title: "Building Resilient Digital Supply Chains",
      category: "Supply Chain"
    },
    {
      title: "Workforce Transformation in the Age of Automation",
      category: "Talent"
    },
    {
      title: "Driving ROI from Cloud Investments",
      category: "Cloud Strategy"
    }
  ];

  return (
    <section
      id="insights"
      className="py-24 lg:py-32 bg-[#FFFFFF]"
      data-testid="insights-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Knowledge
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128]"
              >
                Insights
              </motion.h2>
            </div>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#4A5568] max-w-md mt-4 lg:mt-0"
            >
              Thought Leadership & Industry Perspectives
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {insights.map((insight, index) => (
              <motion.article
                key={insight.title}
                variants={fadeInUp}
                className="insight-card group cursor-pointer"
                data-testid={`insight-card-${index}`}
              >
                <div className="p-8">
                  <span className="text-xs font-mono uppercase tracking-wider text-[#B87333] mb-4 block">
                    {insight.category}
                  </span>
                  <h3 className="insight-title text-2xl font-heading font-semibold text-[#0A1128] mb-4 transition-colors duration-300">
                    {insight.title}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-[#4A5568] group-hover:text-[#B87333] transition-colors duration-300">
                    Read More
                    <ArrowRight
                      size={16}
                      className="transform group-hover:translate-x-1 transition-transform duration-300"
                    />
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Careers Section
const CareersSection = () => {
  const benefits = [
    "Competitive compensation packages",
    "Flexible remote and hybrid work models",
    "Continuous learning and leadership development",
    "Opportunities on global, high-impact projects"
  ];

  return (
    <section
      id="careers"
      className="py-24 lg:py-32 bg-[#F8F7F4]"
      data-testid="careers-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Join Us
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
              >
                Careers
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#4A5568] mb-8">
                Join GentryTechnologies Consulting Group. Work with industry leaders on
                cutting-edge transformation initiatives.
              </motion.p>

              <motion.div variants={fadeInUp}>
                <h3 className="text-xl font-heading font-semibold text-[#0A1128] mb-6">
                  What We Offer
                </h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="career-benefit"
                      data-testid={`career-benefit-${index}`}
                    >
                      <GraduationCap
                        size={24}
                        weight="duotone"
                        className="text-[#B87333]"
                      />
                      <span className="text-[#4A5568]">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-8">
                <a
                  href="#contact"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="careers-cta"
                >
                  Explore Opportunities
                  <ArrowRight size={16} />
                </a>
              </motion.div>
            </div>

            {/* Right - Image */}
            <motion.div variants={fadeInUp} className="hidden lg:block">
              <div className="aspect-[4/3] rounded-sm overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/37347/office-sitting-room-executive-sitting.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern office space"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, service_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Enterprise Transformation",
    "Technology Consulting",
    "Talent Solutions",
    "Global Delivery",
    "Managed Services"
  ];

  return (
    <section
      id="contact"
      className="py-24 lg:py-32 bg-[#FFFFFF]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <motion.span variants={fadeInUp} className="overline block mb-4">
                Get In Touch
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
              >
                Contact Us
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-[#4A5568] mb-12"
              >
                Let's Transform Together
              </motion.p>

              <motion.div variants={fadeInUp} className="space-y-6">
                <div className="flex items-center gap-4">
                  <Envelope
                    size={24}
                    weight="duotone"
                    className="text-[#B87333]"
                  />
                  <span className="text-[#4A5568]">info@gentrytechnologies.com</span>
                </div>
                <div className="flex items-center gap-4">
                  <MapPin size={24} weight="duotone" className="text-[#B87333]" />
                  <span className="text-[#4A5568]">
                    Headquarters: Sacramento, California
                  </span>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-12">
                <p className="text-sm text-[#718096] mb-4">Global Presence:</p>
                <p className="text-[#4A5568]">
                  United States | Canada | Europe | India
                </p>
              </motion.div>
            </div>

            {/* Right - Form */}
            <motion.div variants={fadeInUp}>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] placeholder:text-[#718096]"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] placeholder:text-[#718096]"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] placeholder:text-[#718096]"
                      data-testid="contact-company"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] placeholder:text-[#718096]"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <Select
                    value={formData.service_interest}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger
                      className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] text-[#718096]"
                      data-testid="contact-service"
                    >
                      <SelectValue placeholder="Service of Interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Textarea
                    name="message"
                    placeholder="How can we help? *"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-transparent border-0 border-b-2 border-[#E2E8F0] rounded-none px-0 focus:ring-0 focus:border-[#B87333] placeholder:text-[#718096] resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Leadership Section
const LeadershipSection = () => {
  const leadership = [
    // Senior VPs
    {
      name: "Melinda May",
      title: "Senior Vice President",
      department: "Enterprise Transformation",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Melinda leads GentryTechnologies' enterprise transformation practice with over 22 years of experience in large-scale ERP implementations across SAP S/4HANA, Oracle Cloud, and Workday. She has directed transformation programs for Fortune 100 clients in manufacturing, healthcare, and financial services, delivering more than $500M in operational efficiencies. A recognized thought leader in digital business reinvention, Melinda holds an MBA from Wharton and speaks regularly at industry conferences on the intersection of AI, ERP, and workforce transformation."
    },
    {
      name: "Marcus Whitfield",
      title: "Senior Vice President",
      department: "Mergers & Acquisitions Advisory",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Marcus heads the M&A Advisory practice, guiding clients through complex due diligence, post-merger integration, and IT carve-outs. Prior to GentryTechnologies, he was a Managing Partner at a global consulting firm where he led over 40 transactions valued at more than $12B combined. Marcus brings deep expertise in synergy realization, ERP consolidation post-acquisition, and enterprise architecture rationalization. He holds an MBA from Kellogg and is a CFA charterholder."
    },
    {
      name: "Anika Ramaswamy",
      title: "Senior Vice President",
      department: "Technology & Innovation",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Anika oversees GentryTechnologies' technology and innovation portfolio, spanning AI/ML platforms, cloud architecture, and cybersecurity. A former CTO at two Fortune 500 subsidiaries, she has scaled engineering organizations from 50 to 500+ across three continents. Her research on intelligent automation has been published in MIT Sloan Management Review, and she serves on the advisory boards of two Series B AI startups. Anika holds a PhD in Computer Science from Carnegie Mellon."
    },
    // VPs
    {
      name: "David Sinclair",
      title: "Vice President",
      department: "ERP Implementation",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "David leads GentryTechnologies' ERP implementation practice, specializing in SAP S/4HANA and Oracle NetSuite deployments for mid-market and enterprise clients. Over 18 years, he has personally directed 25+ full-lifecycle ERP programs across manufacturing, distribution, and life sciences. Known for his pragmatic delivery methodology, David has consistently delivered projects 15-20% under budget while accelerating time-to-value. He is a certified SAP Solution Architect and holds a BS in Industrial Engineering from Georgia Tech."
    },
    {
      name: "Priya Chen-Patel",
      title: "Vice President",
      department: "Post-Merger Integration",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Priya specializes in post-merger integration, having led over 30 integrations ranging from $50M tuck-ins to $8B mega-mergers. Her expertise spans Day 1 readiness, systems harmonization, TSA exit planning, and cultural integration. Before joining GentryTechnologies, Priya was a Director at a Big 4 firm's M&A practice. She is a frequent speaker at CFO forums on integration value capture and holds an MBA from INSEAD."
    },
    {
      name: "Jonathan Reeves",
      title: "Vice President",
      department: "Cloud & Data Platforms",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Jonathan leads the cloud and data platforms practice, architecting modern data ecosystems on AWS, Azure, and Google Cloud. He has migrated more than 150 enterprise workloads and built data platforms handling petabyte-scale analytics for global retailers and banks. A recognized expert in FinOps and cloud economics, Jonathan has helped clients realize 30-45% reductions in cloud spend through architectural optimization. He holds professional certifications across all three major cloud providers."
    },
    // Managing Directors
    {
      name: "Sarah O'Brien",
      title: "Managing Director",
      department: "Healthcare & Life Sciences",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Sarah leads GentryTechnologies' Healthcare & Life Sciences vertical, bringing 20 years of experience serving payers, providers, and pharma clients. She has orchestrated Epic and Cerner EHR implementations, led three major provider network M&A integrations, and pioneered AI-driven claims processing programs saving clients $200M+ annually. Sarah is a board member of the Healthcare Information Management Systems Society (HIMSS) and holds an MHA from Johns Hopkins."
    },
    {
      name: "Rafael Delgado",
      title: "Managing Director",
      department: "Financial Services",
      image: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Rafael leads the Financial Services practice, advising global banks, insurers, and asset managers on digital transformation and regulatory technology. His track record includes leading core banking platform modernizations for two top-25 US banks and implementing enterprise risk platforms at three global insurers. Rafael brings deep expertise in Basel III, IFRS 17, and CCAR compliance transformations. He holds an MBA from Chicago Booth and is a FRM charterholder."
    },
    {
      name: "Elena Kowalski",
      title: "Managing Director",
      department: "Manufacturing & Supply Chain",
      image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
      bio: "Elena heads the Manufacturing & Supply Chain vertical, with 19 years of experience transforming industrial operations across automotive, aerospace, and consumer goods. She has led SAP S/4HANA and Oracle SCM Cloud implementations at 12 global manufacturers, driving 25-35% improvements in inventory turns and order fulfillment. Elena is a certified Six Sigma Master Black Belt and holds an MS in Supply Chain Management from MIT."
    }
  ];

  const groupedLeadership = {
    "Senior Vice Presidents": leadership.slice(0, 3),
    "Vice Presidents": leadership.slice(3, 6),
    "Managing Directors": leadership.slice(6, 9)
  };

  return (
    <section
      id="leadership"
      className="py-24 lg:py-32 bg-[#FFFFFF]"
      data-testid="leadership-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-16">
            <motion.span variants={fadeInUp} className="overline block mb-4">
              Our Team
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-4xl lg:text-5xl font-heading font-semibold text-[#0A1128] mb-6"
            >
              Meet Our Leadership
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#4A5568] max-w-2xl mx-auto"
            >
              Seasoned executives driving transformation across ERP, M&A, and enterprise technology.
            </motion.p>
          </div>

          {Object.entries(groupedLeadership).map(([groupTitle, members]) => (
            <div key={groupTitle} className="mb-16 last:mb-0">
              <motion.h3
                variants={fadeInUp}
                className="text-2xl font-heading font-semibold text-[#0A1128] mb-8 pb-3 border-b border-[#E2E8F0]"
              >
                {groupTitle}
              </motion.h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {members.map((member, index) => (
                  <motion.div
                    key={member.name}
                    variants={fadeInUp}
                    className="value-card"
                    data-testid={`leader-${member.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="aspect-square overflow-hidden mb-6 rounded-sm">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="overline mb-2">{member.department}</p>
                    <h4 className="text-xl font-heading font-semibold text-[#0A1128] mb-1">
                      {member.name}
                    </h4>
                    <p className="text-sm text-[#B87333] font-medium mb-4">
                      {member.title}
                    </p>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                      {member.bio}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Employment Verification Modal
const EmploymentVerificationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    requester_name: "",
    requester_company: "",
    requester_email: "",
    requester_phone: "",
    employee_name: "",
    employee_id: "",
    verification_type: "",
    additional_info: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, verification_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/employment-verification`, formData);
      toast.success("Employment verification request submitted successfully.");
      setFormData({
        requester_name: "",
        requester_company: "",
        requester_email: "",
        requester_phone: "",
        employee_name: "",
        employee_id: "",
        verification_type: "",
        additional_info: ""
      });
      onClose();
    } catch (error) {
      console.error("Error submitting verification request:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const verificationTypes = [
    "Employment Dates",
    "Job Title/Position",
    "Salary Verification",
    "Employment Status",
    "Full Verification (All Details)"
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-semibold text-[#0A1128]">
            Employment Verification Request
          </DialogTitle>
          <DialogDescription className="text-[#4A5568]">
            Submit a request to verify employment information. Requests are sent to our HR department.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-4" data-testid="verification-form">
          <div className="border-b border-[#E2E8F0] pb-4">
            <h3 className="text-sm font-semibold text-[#0A1128] mb-3">Requester Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                type="text"
                name="requester_name"
                placeholder="Your Name *"
                value={formData.requester_name}
                onChange={handleChange}
                required
                className="bg-white"
                data-testid="verification-requester-name"
              />
              <Input
                type="text"
                name="requester_company"
                placeholder="Your Company *"
                value={formData.requester_company}
                onChange={handleChange}
                required
                className="bg-white"
                data-testid="verification-requester-company"
              />
              <Input
                type="email"
                name="requester_email"
                placeholder="Your Email *"
                value={formData.requester_email}
                onChange={handleChange}
                required
                className="bg-white"
                data-testid="verification-requester-email"
              />
              <Input
                type="tel"
                name="requester_phone"
                placeholder="Your Phone"
                value={formData.requester_phone}
                onChange={handleChange}
                className="bg-white"
                data-testid="verification-requester-phone"
              />
            </div>
          </div>

          <div className="border-b border-[#E2E8F0] pb-4">
            <h3 className="text-sm font-semibold text-[#0A1128] mb-3">Employee Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                type="text"
                name="employee_name"
                placeholder="Employee Name *"
                value={formData.employee_name}
                onChange={handleChange}
                required
                className="bg-white"
                data-testid="verification-employee-name"
              />
              <Input
                type="text"
                name="employee_id"
                placeholder="Employee ID (if known)"
                value={formData.employee_id}
                onChange={handleChange}
                className="bg-white"
                data-testid="verification-employee-id"
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#0A1128] mb-3">Verification Details</h3>
            <Select
              value={formData.verification_type}
              onValueChange={handleSelectChange}
            >
              <SelectTrigger className="bg-white mb-4" data-testid="verification-type">
                <SelectValue placeholder="Select Verification Type *" />
              </SelectTrigger>
              <SelectContent>
                {verificationTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Textarea
              name="additional_info"
              placeholder="Additional Information or Special Requests"
              value={formData.additional_info}
              onChange={handleChange}
              rows={3}
              className="bg-white resize-none"
              data-testid="verification-additional-info"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.verification_type}
              className="flex-1 btn-primary"
              data-testid="verification-submit"
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  return (
    <footer
      className="py-16 bg-[#0A1128]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <span className="font-heading text-2xl font-semibold text-white block mb-4">
              GentryTechnologies
            </span>
            <p className="text-gray-400 max-w-md">
              GentryTechnologies Consulting Group partners with forward-thinking
              organizations to deliver AI-driven innovation and enterprise
              transformation.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a href="#about" className="footer-link">
                  About Us
                </a>
              </li>
              <li>
                <a href="#services" className="footer-link">
                  Services
                </a>
              </li>
              <li>
                <a href="#industries" className="footer-link">
                  Industries
                </a>
              </li>
              <li>
                <a href="#careers" className="footer-link">
                  Careers
                </a>
              </li>
              <li>
                <button
                  onClick={() => setVerificationModalOpen(true)}
                  className="footer-link text-left hover:text-[#B87333] transition-colors"
                  data-testid="employment-verification-link"
                >
                  Employment Verification
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-gray-400">
              <li>info@gentrytechnologies.com</li>
              <li>Sacramento, California</li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a
                href="#"
                className="text-gray-400 hover:text-[#B87333] transition-colors"
                aria-label="LinkedIn"
              >
                <LinkedinLogo size={24} weight="fill" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#B87333] transition-colors"
                aria-label="Twitter"
              >
                <TwitterLogo size={24} weight="fill" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} GentryTechnologies Consulting Group. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="footer-link">
              Privacy Policy
            </a>
            <a href="#" className="footer-link">
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      {/* Employment Verification Modal */}
      <EmploymentVerificationModal
        isOpen={verificationModalOpen}
        onClose={() => setVerificationModalOpen(false)}
      />
    </footer>
  );
};

// Home Page Component
const Home = () => {
  return (
    <div className="overflow-x-hidden">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ApproachSection />
      <IndustriesSection />
      <WhyGentryTechSection />
      <ClientImpactSection />
      <LeadershipSection />
      <InsightsSection />
      <CareersSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
